import { useShallow } from 'zustand/react/shallow';
import { useEffect, useRef } from 'react';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import { useCatalogStore } from '../../store/catalogStore';
import ProductCard from '../ProductCard/ProductCard';
import './ProductGrid.css';
import { ProductCardItem } from '../../types/api';

export function ProductCards({ items }: { items: ProductCardItem[] }) {
  return (
    <ul className="product-grid__grid">
      {items.map((result) => (
        <ProductCard key={result.item_id} result={result} />
      ))}
    </ul>
  );
}

export default function ProductGrid() {
  const { items, loading, error, visibleCount, fetchSearch, query, showMore } = useCatalogStore(
    useShallow((s) => ({
      items: s.items,
      loading: s.loading,
      error: s.error,
      visibleCount: s.visibleCount,
      fetchSearch: s.fetchSearch,
      query: s.query,
      showMore: s.showMore,
    })),
  );

  useScrollRestoration('search');

  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;
  const sentinel = useRef<HTMLDivElement>(null);

  // One observer covers both "scrolled near the end" and "results don't fill the viewport".
  // Re-observing after each reveal re-checks whether the sentinel is still on screen.
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) showMore();
      },
      { rootMargin: '300px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, visibleCount, showMore]);

  return (
    <section className="product-grid" aria-label="Search results">
      {loading ? (
        <div className="product-grid__loading">Loading products…</div>
      ) : error ? (
        <div className="product-grid__error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => fetchSearch(query ?? '')}>
            Try again
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="product-grid__empty">No products yet. Try searching above.</div>
      ) : (
        <>
          <ProductCards items={visibleItems} />
          {hasMore && <div ref={sentinel} aria-hidden="true" />}
        </>
      )}
    </section>
  );
}
